import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { API, auth, money, fmtDate, inputCls, Field, Modal, PageHead, PrimaryBtn, FinanceEmpty, StatusBadge, Paginator, PAY_METHODS, PrintStyle, printArea, exportPdf, errMsg } from './shared';
import Icon from '../../../components/dashboard/DashboardIcons';
import PremiumDatePicker from '../../../components/ui/PremiumDatePicker';
import { canUseWhatsApp } from '../../../lib/whatsapp';
import { WhatsAppMessageModal } from '../../../components/whatsapp/WhatsAppMessageModal';

export default function InvoicesPage() {
  const { club, user, selectedSeasonId } = useOutletContext() || {};
  const seasonQS = selectedSeasonId && selectedSeasonId !== 'all' ? `&season_id=${selectedSeasonId}` : '';
  const canWhatsApp = canUseWhatsApp(user);
  const [waInvoice, setWaInvoice] = useState(null);
  const invoicePrintRef = useRef(null);
  const receiptPrintRef = useRef(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [vatSettings, setVatSettings] = useState({ vat_registered: false, vat_rate: 15 });
  const [identity, setIdentity] = useState({ logo_file_id: null, club_name: null, phone: '', email: '' });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ client_name: '', issue_date: '', due_date: '', notes: '', invoice_title: '', items: [{ description: '', quantity: 1, unit_price: '' }] });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payInv, setPayInv] = useState(null);
  const [payForm, setPayForm] = useState({});
  const [payError, setPayError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [printInv, setPrintInv] = useState(null);
  const [printReceipt, setPrintReceipt] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [toast, setToast] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [res, settingsRes] = await Promise.all([
        axios.get(`${API}/club-invoices?page=${page}&limit=15${statusFilter ? `&status=${statusFilter}` : ''}${seasonQS}`, auth()),
        axios.get(`${API}/club-settings`, auth()).catch(() => null),
      ]);
      setData(res.data);
      if (settingsRes?.data) {
        setVatSettings({ vat_registered: !!settingsRes.data.vat_registered, vat_rate: settingsRes.data.vat_rate || 15 });
        setIdentity({
          logo_file_id: settingsRes.data.logo_file_id || null,
          club_name: settingsRes.data.club_name || null,
          phone: settingsRes.data.contact_info?.phone || '',
          email: settingsRes.data.contact_info?.email || '',
        });
      }
    } catch (e) { setError(e.response?.status === 403 ? 'لا تملك صلاحية الوصول للفواتير' : 'تعذر التحميل'); }
    setLoading(false);
  }, [page, statusFilter, seasonQS]);
  useEffect(() => { load(); }, [load]);

  const openCreateModal = () => {
    setForm({ client_name: '', issue_date: '', due_date: '', notes: '', invoice_title: vatSettings.vat_registered ? 'فاتورة ضريبية' : 'فاتورة اشتراك', items: [{ description: '', quantity: 1, unit_price: '' }] });
    setFormError(null);
    setModal(true);
  };

  const setItem = (i, k, v) => { const items = [...form.items]; items[i] = { ...items[i], [k]: v }; setForm({ ...form, items }); };
  const subtotal = form.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const vat = vatSettings.vat_registered ? subtotal * (vatSettings.vat_rate / 100) : 0;

  const submit = async (e) => {
    e.preventDefault();
    const items = form.items.filter((i) => i.description && i.unit_price);
    if (!form.client_name || !form.issue_date || items.length === 0) { setFormError('يرجى تعبئة العميل والتاريخ وبند واحد على الأقل'); return; }
    setSaving(true); setFormError(null);
    try {
      await axios.post(`${API}/club-invoices`, {
        client_name: form.client_name, issue_date: form.issue_date,
        due_date: form.due_date || undefined, notes: form.notes || undefined,
        invoice_title: form.invoice_title?.trim() || undefined,
        items: items.map((i) => ({ description: i.description, quantity: parseFloat(i.quantity) || 1, unit_price: parseFloat(i.unit_price) })),
      }, auth());
      setModal(false); notify('تم إنشاء الفاتورة'); load();
      setForm({ client_name: '', issue_date: '', due_date: '', notes: '', invoice_title: '', items: [{ description: '', quantity: 1, unit_price: '' }] });
    } catch (err) { setFormError(errMsg(err, 'فشل الإنشاء')); }
    setSaving(false);
  };

  const openPay = (inv) => { setPayInv(inv); setPayForm({ amount: '', payment_method: 'cash', reference_number: '', notes: '', payment_date: new Date().toISOString().slice(0, 10), attachment_file_id: null }); setPayError(null); };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/files/upload`, fd, auth());
      setPayForm((f) => ({ ...f, attachment_file_id: res.data?.file_id || res.data?.id }));
    } catch (err) { setPayError(errMsg(err, 'فشل رفع المرفق')); }
    setUploading(false);
  };

  const recordPayment = async () => {
    setPayError(null);
    const amount = parseFloat(payForm.amount);
    if (!amount || amount <= 0) { setPayError('أدخل مبلغاً صحيحاً'); return; }
    try {
      await axios.put(`${API}/club-invoices/${payInv.id}/payment`, {
        amount, payment_date: payForm.payment_date, payment_method: payForm.payment_method,
        reference_number: payForm.reference_number || undefined, notes: payForm.notes || undefined,
        attachment_file_id: payForm.attachment_file_id || undefined,
      }, auth());
      notify('تم تسجيل الدفعة وإصدار سند القبض'); setPayInv(null); load();
    } catch (err) { setPayError(errMsg(err, 'فشل تسجيل الدفعة')); }
  };

  const [cancelInv, setCancelInv] = useState(null);
  const cancel = async () => {
    try { await axios.put(`${API}/club-invoices/${cancelInv.id}/cancel`, {}, auth()); notify('تم إلغاء الفاتورة'); setCancelInv(null); load(); }
    catch (err) { notify(errMsg(err, 'فشل الإلغاء')); setCancelInv(null); }
  };

  if (error) return <div className="rounded-xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-600" data-testid="invoices-error">{error}</div>;

  return (
    <div className="mx-auto max-w-7xl space-y-4" data-testid="invoices-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <PageHead title="الفواتير الداخلية" desc="فواتير تصدرها المنشأة لعملائها (رعايات، إيجارات، خدمات)"
        action={<PrimaryBtn onClick={openCreateModal} data-testid="invoice-add-btn"><Icon name="plus" className="w-4 h-4" /> فاتورة جديدة</PrimaryBtn>} />

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-600" data-testid="invoice-status-filter">
            <option value="">كل الحالات</option>
            <option value="unpaid">غير مدفوعة</option><option value="partial">مدفوعة جزئياً</option><option value="paid">مدفوعة</option><option value="cancelled">ملغاة</option>
          </select>
          <span className="text-xs text-slate-400">الإجمالي: {data.total} فاتورة</span>
        </div>
        {loading ? <div className="space-y-2 p-4">{[1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : data.items.length === 0 ? <div className="p-6"><FinanceEmpty text="لا توجد فواتير — أنشئ أول فاتورة للمنشأة" /></div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">الرقم</th><th className="px-4 py-2.5 font-semibold">العميل</th>
                  <th className="px-4 py-2.5 font-semibold">الإجمالي</th><th className="hidden px-4 py-2.5 font-semibold md:table-cell">المتبقي</th>
                  <th className="hidden px-4 py-2.5 font-semibold lg:table-cell">الإصدار</th><th className="px-4 py-2.5 font-semibold">الحالة</th><th className="px-4 py-2.5 font-semibold">إجراءات</th>
                </tr></thead>
                <tbody>
                  {data.items.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="invoice-row">
                      <td className="px-4 py-2.5 font-bold text-slate-800" dir="ltr">{inv.invoice_number}</td>
                      <td className="px-4 py-2.5 text-slate-700">{inv.client_name}</td>
                      <td className="px-4 py-2.5 font-bold text-slate-900" dir="rtl">{money(inv.total)}</td>
                      <td className="hidden px-4 py-2.5 text-amber-600 md:table-cell" dir="rtl">{money(inv.total - (inv.paid_amount || 0))}</td>
                      <td className="hidden px-4 py-2.5 text-slate-600 lg:table-cell">{fmtDate(inv.issue_date)}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={inv.status} overdue={inv.is_overdue} /></td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setPrintInv(inv)} title="عرض وطباعة" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" data-testid="invoice-print-btn"><Icon name="document" className="w-4 h-4" /></button>
                          {['unpaid', 'partial'].includes(inv.status) && <button onClick={() => openPay(inv)} title="تسجيل دفعة" className="rounded-lg p-1.5 text-slate-500 hover:bg-teal-50 hover:text-teal-700" data-testid="invoice-pay-btn"><Icon name="banknotes" className="w-4 h-4" /></button>}
                          {canWhatsApp && <button onClick={() => setWaInvoice(inv)} title="مراسلة عبر واتساب" className="rounded-lg p-1.5 text-slate-500 hover:bg-emerald-50 hover:text-emerald-700" data-testid="invoice-whatsapp-btn"><Icon name="whatsapp" className="w-4 h-4" /></button>}
                          {inv.status !== 'paid' && inv.status !== 'cancelled' && <button onClick={() => setCancelInv(inv)} title="إلغاء" className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="invoice-cancel-btn"><Icon name="x" className="w-4 h-4" /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        <Paginator page={page} total={data.total} limit={15} onChange={setPage} />
      </div>

      <Modal open={modal} title="فاتورة جديدة" onClose={() => setModal(false)} wide>
        {formError && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="invoice-form-error">{formError}</div>}
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="العميل / الجهة" required><input className={inputCls} value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="invoice-input-client" /></Field>
            <Field label="تاريخ الإصدار" required><PremiumDatePicker value={form.issue_date} onChange={(v) => setForm({ ...form, issue_date: v })} testId="invoice-input-issue" /></Field>
            <Field label="تاريخ الاستحقاق"><PremiumDatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testId="invoice-input-due" /></Field>
          </div>
          <Field label="عنوان الفاتورة">
            <input className={inputCls} value={form.invoice_title} onChange={(e) => setForm({ ...form, invoice_title: e.target.value })}
              placeholder="مثال: فاتورة رسوم اشتراك لاعب، فاتورة حجز ملعب، فاتورة اشتراك تسيار" data-testid="invoice-input-title" />
            <span className="mt-1 block text-[11px] text-slate-400">يظهر هذا العنوان في مستند الفاتورة المطبوع/PDF، ويمكن تعديله حسب الغرض من الفاتورة</span>
          </Field>
          <div className="space-y-2">
            <span className="text-xs font-semibold text-slate-600">البنود</span>
            {form.items.map((it, i) => (
              <div key={i} className="flex gap-2">
                <input className={inputCls} placeholder="الوصف" value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} data-testid={`invoice-item-desc-${i}`} />
                <input className={`${inputCls} w-20`} dir="ltr" type="number" min="0.5" step="0.5" placeholder="كمية" value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} />
                <input className={`${inputCls} w-28`} dir="ltr" type="number" min="0" step="0.01" placeholder="سعر الوحدة" value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value)} data-testid={`invoice-item-price-${i}`} />
                {form.items.length > 1 && <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })} className="text-red-500"><Icon name="x" className="w-4 h-4" /></button>}
              </div>
            ))}
            <button type="button" onClick={() => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: '' }] })} className="text-xs font-semibold text-teal-700" data-testid="invoice-add-item">+ إضافة بند</button>
          </div>
          <div className="rounded-lg bg-slate-50 px-4 py-3 text-sm" data-testid="invoice-totals">
            <div className="flex justify-between"><span className="text-slate-500">{vatSettings.vat_registered ? 'المجموع قبل الضريبة' : 'المجموع'}</span><span dir="rtl">{money(subtotal)}</span></div>
            {vatSettings.vat_registered ? (
              <div className="flex justify-between"><span className="text-slate-500">ضريبة القيمة المضافة {vatSettings.vat_rate}%</span><span dir="rtl">{money(vat)}</span></div>
            ) : (
              <p className="text-xs text-slate-400">المنشأة غير مسجّلة في ضريبة القيمة المضافة — لا تُضاف ضريبة (يمكن تفعيلها من الإعدادات ← الضريبة)</p>
            )}
            <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 font-bold"><span>الإجمالي</span><span dir="rtl">{money(subtotal + vat)}</span></div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" disabled={saving} data-testid="invoice-form-submit">{saving ? 'جارٍ الإنشاء...' : 'إنشاء الفاتورة'}</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(payInv)} title={`تسجيل دفعة — ${payInv?.invoice_number}`} onClose={() => setPayInv(null)}>
        {payError && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="invoice-pay-error">{payError}</div>}
        <p className="mb-2 text-sm text-slate-500">المتبقي: <span className="font-bold text-slate-800" dir="rtl">{money((payInv?.total || 0) - (payInv?.paid_amount || 0))}</span></p>
        <div className="space-y-3">
          <Field label="مبلغ الدفعة (ر.س)" required><input className={inputCls} dir="ltr" type="number" min="0.01" step="0.01" value={payForm.amount || ''} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="invoice-pay-amount" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="تاريخ الدفعة"><PremiumDatePicker value={payForm.payment_date || ''} onChange={(v) => setPayForm({ ...payForm, payment_date: v })} testId="invoice-pay-date" /></Field>
            <Field label="طريقة الدفع">
              <select className={inputCls} value={payForm.payment_method || 'cash'} onChange={(e) => setPayForm({ ...payForm, payment_method: e.target.value })} data-testid="invoice-pay-method">
                {Object.entries(PAY_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </div>
          <Field label="رقم المرجع / التحويل"><input className={inputCls} dir="ltr" value={payForm.reference_number || ''} onChange={(e) => setPayForm({ ...payForm, reference_number: e.target.value })} data-testid="invoice-pay-reference" /></Field>
          <Field label="ملاحظات"><textarea rows={2} className={inputCls} value={payForm.notes || ''} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} /></Field>
          <Field label="مرفق الإيصال (اختياري)">
            <div className="flex items-center gap-2">
              <label className="cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-teal-300">
                {uploading ? 'جارٍ الرفع...' : 'رفع مرفق'}
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={uploadAttachment} data-testid="invoice-pay-attachment-input" />
              </label>
              {payForm.attachment_file_id && <span className="text-xs text-teal-700">تم الرفع ✓</span>}
            </div>
          </Field>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setPayInv(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <PrimaryBtn onClick={recordPayment} disabled={!payForm.amount} data-testid="invoice-pay-confirm">تسجيل الدفعة</PrimaryBtn>
        </div>
      </Modal>

      <Modal open={Boolean(cancelInv)} title={`إلغاء الفاتورة ${cancelInv?.invoice_number || ''}`} onClose={() => setCancelInv(null)}>
        <p className="text-sm text-slate-600">هل أنت متأكد من إلغاء هذه الفاتورة؟ لا يمكن الدفع عليها بعد الإلغاء.</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setCancelInv(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">تراجع</button>
          <button onClick={cancel} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700" data-testid="invoice-cancel-confirm">تأكيد الإلغاء</button>
        </div>
      </Modal>

      {printInv && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <PrintStyle />
          <div className="absolute inset-0 bg-black/50 print:hidden" onClick={() => setPrintInv(null)} />
          <div className="relative w-full max-w-lg">
            <div className="print-area rounded-xl bg-white p-6 shadow-2xl" data-testid="invoice-print-view" ref={invoicePrintRef}>
              <div className="mb-4 flex items-start justify-between gap-3 border-b-2 border-[#16233b] pb-3">
                <div className="flex items-center gap-3">
                  {identity.logo_file_id && <img src={`${API}/files/${identity.logo_file_id}`} alt="شعار المنشأة" className="h-12 w-12 flex-shrink-0 rounded-lg bg-white object-contain" />}
                  <div>
                    <div className="text-base font-bold text-slate-900">{printInv.legal_name_snapshot || identity.club_name || club?.name || 'المنشأة'}</div>
                    <div className="mt-0.5 text-sm font-semibold text-teal-700">{printInv.invoice_title || (printInv.is_tax_invoice ? 'فاتورة ضريبية' : 'فاتورة اشتراك')}</div>
                    <div className="text-xs text-slate-500" dir="ltr">{printInv.invoice_number}</div>
                    {printInv.is_tax_invoice && printInv.vat_number_snapshot && <div className="text-xs text-slate-400" dir="ltr">الرقم الضريبي: {printInv.vat_number_snapshot}</div>}
                  </div>
                </div>
                <StatusBadge status={printInv.status} overdue={printInv.is_overdue} />
              </div>
              <div className="mb-3 grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-400">العميل:</span> <span className="font-semibold">{printInv.client_name}</span></div>
                <div><span className="text-slate-400">الإصدار:</span> {fmtDate(printInv.issue_date)}</div>
                {printInv.due_date && <div><span className="text-slate-400">الاستحقاق:</span> {fmtDate(printInv.due_date)}</div>}
              </div>
              <table className="mb-3 w-full text-sm">
                <thead><tr className="border-b border-slate-200 text-right text-xs text-slate-500"><th className="py-1.5">الوصف</th><th className="py-1.5">الكمية</th><th className="py-1.5">السعر</th><th className="py-1.5">الإجمالي</th></tr></thead>
                <tbody>{printInv.items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-50"><td className="py-1.5">{it.description}</td><td className="py-1.5">{it.quantity}</td><td className="py-1.5" dir="rtl">{money(it.unit_price)}</td><td className="py-1.5 font-semibold" dir="rtl">{money(it.quantity * it.unit_price)}</td></tr>
                ))}</tbody>
              </table>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">{printInv.is_tax_invoice ? 'المجموع قبل الضريبة' : 'المجموع'}</span><span dir="rtl">{money(printInv.subtotal)}</span></div>
                {printInv.is_tax_invoice && <div className="flex justify-between"><span className="text-slate-500">الضريبة ({printInv.vat_rate_snapshot}%)</span><span dir="rtl">{money(printInv.vat_amount)}</span></div>}
                <div className="flex justify-between border-t border-slate-200 pt-1 font-bold"><span>الإجمالي</span><span dir="rtl">{money(printInv.total)}</span></div>
                <div className="flex justify-between text-teal-700"><span>المدفوع</span><span dir="rtl">{money(printInv.paid_amount || 0)}</span></div>
              </div>
              {(printInv.payments || []).length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-2" data-testid="invoice-payments-history">
                  <div className="mb-1 text-xs font-bold text-slate-600">سجل الدفعات</div>
                  {printInv.payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-1 text-xs">
                      <span className="text-slate-500">{p.receipt_number} — {fmtDate(p.payment_date)} — {PAY_METHODS[p.payment_method] || p.payment_method}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800" dir="rtl">{money(p.amount)}</span>
                        <button onClick={() => setPrintReceipt({ ...p, invoice: printInv })} className="text-teal-700 underline print:hidden" data-testid="invoice-receipt-print-btn">سند القبض</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div data-testid="report-footer" data-show-page-number="0" className="mt-4 flex items-center justify-between border-t border-slate-100 pt-2 text-[10px] text-slate-400">
                <span>{identity.phone || identity.email ? `${identity.phone}${identity.phone && identity.email ? ' · ' : ''}${identity.email}` : (identity.club_name || club?.name || 'المنشأة')}</span>
                <span>تاريخ الإصدار: {fmtDate(printInv.issue_date)}</span>
              </div>
            </div>
            <div className="mt-3 flex justify-center gap-2 print:hidden">
              <PrimaryBtn onClick={printArea} data-testid="invoice-do-print"><Icon name="document" className="w-4 h-4" /> طباعة</PrimaryBtn>
              <button onClick={async () => { setPdfExporting(true); try { await exportPdf(invoicePrintRef.current, `فاتورة-${printInv.invoice_number}`); } finally { setPdfExporting(false); } }} disabled={pdfExporting} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:border-teal-300 disabled:opacity-50" data-testid="invoice-download-pdf-btn">{pdfExporting ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-teal-600" /> جاري التصدير...</> : <><Icon name="download" className="w-4 h-4" /> تنزيل PDF</>}</button>
              <button onClick={() => setPrintInv(null)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-600">إغلاق</button>
            </div>
          </div>
        </div>
      )}

      {waInvoice && (
        <WhatsAppMessageModal
          open={Boolean(waInvoice)}
          onClose={() => setWaInvoice(null)}
          recipient={{ id: waInvoice.id, name: waInvoice.client_name, phone: '', type: 'other' }}
          variables={{
            guardian_name: waInvoice.client_name,
            player_name: waInvoice.client_name,
            remaining_amount: Math.max(waInvoice.total - (waInvoice.paid_amount || 0), 0),
            paid_amount: waInvoice.paid_amount || 0,
            payment_reference: waInvoice.invoice_number,
          }}
          relatedType="invoice"
          relatedId={waInvoice.id}
          allowRecipientSearch
        />
      )}

      {printReceipt && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <PrintStyle />
          <div className="absolute inset-0 bg-black/60 print:hidden" onClick={() => setPrintReceipt(null)} />
          <div className="relative w-full max-w-sm">
            <div className="print-area rounded-xl bg-white p-6 shadow-2xl" data-testid="invoice-receipt-print-view" ref={receiptPrintRef}>
              <div className="mb-3 border-b-2 border-[#16233b] pb-3 text-center">
                {identity.logo_file_id && <img src={`${API}/files/${identity.logo_file_id}`} alt="شعار المنشأة" className="mx-auto mb-1.5 h-10 w-10 rounded-lg bg-white object-contain" />}
                <div className="text-base font-bold text-slate-900">{identity.club_name || club?.name || 'المنشأة'}</div>
                <div className="text-sm font-semibold text-teal-700">سند قبض</div>
                <div className="text-xs text-slate-500" dir="ltr">{printReceipt.receipt_number}</div>
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">العميل</span><span className="font-semibold">{printReceipt.invoice?.client_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">مقابل فاتورة</span><span dir="ltr">{printReceipt.invoice?.invoice_number}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">التاريخ</span><span>{fmtDate(printReceipt.payment_date)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">طريقة الدفع</span><span>{PAY_METHODS[printReceipt.payment_method] || printReceipt.payment_method}</span></div>
                {printReceipt.reference_number && <div className="flex justify-between"><span className="text-slate-500">رقم المرجع</span><span dir="ltr">{printReceipt.reference_number}</span></div>}
                <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 text-base font-bold"><span>المبلغ المستلم</span><span dir="rtl">{money(printReceipt.amount)}</span></div>
              </div>
              <div data-testid="report-footer" data-show-page-number="0" className="mt-4 border-t border-slate-100 pt-2 text-center text-[10px] text-slate-400">
                <span>{identity.phone || identity.email ? `${identity.phone}${identity.phone && identity.email ? ' · ' : ''}${identity.email}` : (identity.club_name || club?.name || 'المنشأة')}</span>
              </div>
            </div>
            <div className="mt-3 flex justify-center gap-2 print:hidden">
              <PrimaryBtn onClick={printArea} data-testid="receipt-do-print"><Icon name="document" className="w-4 h-4" /> طباعة</PrimaryBtn>
              <button onClick={async () => { setPdfExporting(true); try { await exportPdf(receiptPrintRef.current, `سند-قبض-${printReceipt.receipt_number}`); } finally { setPdfExporting(false); } }} disabled={pdfExporting} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:border-teal-300 disabled:opacity-50" data-testid="receipt-download-pdf-btn">{pdfExporting ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-teal-600" /> جاري التصدير...</> : <><Icon name="download" className="w-4 h-4" /> تنزيل PDF</>}</button>
              <button onClick={() => setPrintReceipt(null)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-600">إغلاق</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
